import { ConfigStateService, LocalizationService } from '@abp/ng.core';
import { ToasterService } from '@abp/ng.theme.shared';
import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormGroup,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { Subject } from 'rxjs';
import { switchMap, takeUntil } from 'rxjs/operators';
import { ConfigurationSettingsDto } from 'src/core/models/configuration-setting/configuration-settings.dto';
import { ConversationDurationRangeModel } from 'src/core/models/settings/conversation-duration-range.model';
import { CAConfigStateService } from 'src/core/services/config/ca-config-state.service';
import { ConfigurationSettingsService } from 'src/core/services/configuration-settings/configuration-settings.service';
import { IntegerValidator } from 'src/core/validators/shared/integer.validator';

const SETTING_NAME = 'ConversationDurationRange.Ranges';
const MIN_RANGES = 2;
const MAX_RANGES = 7;

@Component({
  selector: 'ca-conversation-duration-range-settings',
  templateUrl: './conversation-duration-range-settings.component.html',
  styleUrls: ['./conversation-duration-range-settings.component.scss'],
  standalone: false,
})
export class ConversationDurationRangeSettingsComponent implements OnInit, OnDestroy {
  form: FormGroup;
  hasOverlapError = false;

  readonly minRanges = MIN_RANGES;
  readonly maxRanges = MAX_RANGES;

  private readonly destroy$ = new Subject<void>();

  get ranges(): FormArray {
    return this.form.get('ranges') as FormArray;
  }

  get canAddRange(): boolean {
    return this.ranges.length < MAX_RANGES;
  }

  get canRemoveRange(): boolean {
    return this.ranges.length > MIN_RANGES;
  }

  constructor(
    private fb: FormBuilder,
    private config: ConfigStateService,
    private configStateService: CAConfigStateService,
    private settingService: ConfigurationSettingsService,
    private toastr: ToasterService,
    private localizationService: LocalizationService,
    private cdr: ChangeDetectorRef,
  ) {
    this.form = this.fb.group({
      ranges: this.fb.array([]),
    });

    this.populateRanges(this.parseRanges(this.config.getSetting(SETTING_NAME)));

    this.ranges.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.hasOverlapError = this.checkOverlap();
    });
  }

  ngOnInit(): void {}

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  trackByIndex(index: number, control: AbstractControl): AbstractControl {
    return control;
  }

  isLastRow(index: number): boolean {
    return index === this.ranges.length - 1;
  }

  isOpenEnded(index: number): boolean {
    return this.isLastRow(index) && this.ranges.at(index).get('maxSecs').value === null;
  }

  onOpenEndedToggle(index: number, event: MatSlideToggleChange): void {
    const checked = event.checked;
    const group = this.ranges.at(index) as FormGroup;
    const maxControl = group.get('maxSecs');

    // Checked -> open-ended (null max). Unchecked -> force a real value.
    maxControl.setValue(checked ? null : 0);
    maxControl.markAsTouched();
    maxControl.markAsDirty();
    group.updateValueAndValidity();
    this.hasOverlapError = this.checkOverlap();
  }

  addRange(): void {
    if (!this.canAddRange) {
      return;
    }

    const group = this.buildRangeGroup({ label: '', minSecs: 0, maxSecs: null });
    this.ranges.push(group);

    // Force the new row's validation directive to exist before marking it dirty.
    this.cdr.detectChanges();

    group.get('label').markAsDirty();
    this.revalidateAllRows();
    this.hasOverlapError = this.checkOverlap();
  }

  removeRange(index: number): void {
    if (!this.canRemoveRange) {
      return;
    }

    this.ranges.removeAt(index);
    this.revalidateAllRows();
    this.hasOverlapError = this.checkOverlap();
  }

  onSubmit(): void {
    this.hasOverlapError = this.checkOverlap();

    const rangeCountValid = this.ranges.length >= MIN_RANGES && this.ranges.length <= MAX_RANGES;

    if (this.form.invalid || this.hasOverlapError || !rangeCountValid) {
      return;
    }

    const payload = this.ranges.controls.map(control => ({
      Label: control.get('label').value,
      MinSecs: control.get('minSecs').value,
      MaxSecs: control.get('maxSecs').value,
    }));

    const dto = {
      settingName: SETTING_NAME,
      settingValue: JSON.stringify(payload),
    } as ConfigurationSettingsDto;

    this.settingService
      .saveSetting([dto])
      .pipe(
        switchMap(() => this.configStateService.refreshAppState()),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: () => {
          this.populateRanges(this.parseRanges(this.config.getSetting(SETTING_NAME)));
          this.toastr.success(
            this.localizationService.instant('AbpSettingManagement::SavedSuccessfully'),
          );
        },
      });
  }

  private populateRanges(models: ConversationDurationRangeModel[]): void {
    this.ranges.clear();
    models.forEach(model => this.ranges.push(this.buildRangeGroup(model)));
    this.revalidateAllRows();
    this.hasOverlapError = this.checkOverlap();
  }

  private buildRangeGroup(model: ConversationDurationRangeModel): FormGroup {
    return this.fb.group(
      {
        label: [model.label, [Validators.required, Validators.maxLength(50)]],
        minSecs: [model.minSecs, [Validators.required, IntegerValidator.minMax(0, null)]],
        maxSecs: [model.maxSecs],
      },
      { validators: [(g: AbstractControl) => this.minMaxValidator(g)] },
    );
  }

  private minMaxValidator(group: AbstractControl): ValidationErrors | null {
    const minControl = group.get('minSecs');
    const maxControl = group.get('maxSecs');
    const min = minControl?.value;
    const max = maxControl?.value;

    if (max === null || max === undefined) {
      // Null max is only valid for the actual last (open-ended) row.
      const index = this.ranges.controls.indexOf(group);

      if (!this.isLastRow(index)) {
        maxControl?.setErrors({ required: true });
        return { required: true };
      }

      maxControl?.setErrors(null);
      return null;
    }

    const invalid = typeof min === 'number' && max <= min;
    maxControl?.setErrors(invalid ? { rangeMinMaxInvalid: true } : null);

    return invalid ? { rangeMinMaxInvalid: true } : null;
  }

  private revalidateAllRows(): void {
    // Last-row status changes on add/remove, so re-run every row's group validator.
    this.ranges.controls.forEach(control => control.updateValueAndValidity({ emitEvent: false }));
  }

  private checkOverlap(): boolean {
    const overlappingFields = this.findOverlappingFields();
    this.markOverlapErrors(overlappingFields);

    return overlappingFields.size > 0;
  }

  private findOverlappingFields(): Map<number, Set<'minSecs' | 'maxSecs'>> {
    const intervals = this.ranges.controls.map((control, index) => {
      const min = control.get('minSecs').value;
      const max = control.get('maxSecs').value;

      return {
        index,
        min,
        max: max === null || max === undefined ? Infinity : max,
      };
    });

    const overlappingFields = new Map<number, Set<'minSecs' | 'maxSecs'>>();

    const markField = (index: number, field: 'minSecs' | 'maxSecs') => {
      if (!overlappingFields.has(index)) {
        overlappingFields.set(index, new Set());
      }
      overlappingFields.get(index).add(field);
    };

    // Check every pair by value, not just adjacent ones, to catch out-of-order overlaps too.
    intervals.forEach((a, i) => {
      intervals.slice(i + 1).forEach(b => {
        if (a.min <= b.max && b.min <= a.max) {
          const aOpenEnded = this.isOpenEnded(a.index);
          const bOpenEnded = this.isOpenEnded(b.index);

          if (aOpenEnded || bOpenEnded) {
            // Open-ended has no real max to blame and overlaps everything - blame only its min.
            markField(aOpenEnded ? a.index : b.index, 'minSecs');
            return;
          }

          // Blame the earlier range's max and the later range's min.
          const [earlier, later] = a.min <= b.min ? [a, b] : [b, a];
          markField(earlier.index, 'maxSecs');
          markField(later.index, 'minSecs');
        }
      });
    });

    return overlappingFields;
  }

  private markOverlapErrors(overlappingFields: Map<number, Set<'minSecs' | 'maxSecs'>>): void {
    this.ranges.controls.forEach((control, index) => {
      const fieldsForRow = overlappingFields.get(index);

      (['minSecs', 'maxSecs'] as const).forEach(fieldName => {
        const fieldControl = control.get(fieldName);
        const errors = { ...(fieldControl.errors ?? {}) };
        delete errors.rangeOverlap;

        if (fieldsForRow?.has(fieldName)) {
          errors.rangeOverlap = true;
        }

        // ngx-validate only renders errors on dirty controls.
        fieldControl.markAsDirty();
        fieldControl.setErrors(Object.keys(errors).length ? errors : null);
      });
    });
  }

  private parseRanges(raw: any): ConversationDurationRangeModel[] {
    const defaults: ConversationDurationRangeModel[] = [
      { label: '0-60s', minSecs: 0, maxSecs: 60 },
      { label: '61-120s', minSecs: 61, maxSecs: 120 },
      { label: '121-240s', minSecs: 121, maxSecs: 240 },
      { label: '241-480s', minSecs: 241, maxSecs: 480 },
      { label: '481s+', minSecs: 481, maxSecs: null },
    ];

    if (!raw) {
      return defaults;
    }

    try {
      const parsed = JSON.parse(raw);

      if (!Array.isArray(parsed) || parsed.length === 0) {
        return defaults;
      }

      return parsed.map(item => ({
        label: item.Label,
        minSecs: item.MinSecs,
        maxSecs: item.MaxSecs === undefined ? null : item.MaxSecs,
      }));
    } catch {
      return defaults;
    }
  }
}
